import { SetCamelToSnakeCaseNested, SetSnakeToCamelCaseNested } from '../types/format.type';
import { ExtractNestedKey } from '../types/object.type';

/**
 * Formats string from snake_case into camelCase
 *
 * @param str
 * @returns
 */
export function toCamel(str: string) {
  // If does not contain underscore, the string will not be considered as snake case
  if (!str.includes('_')) return str;

  return str.toLowerCase().replace(/[_]+([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

/**
 * Formats all keys into camelCase
 *
 * @param data
 * @returns
 */
export function toCamelKeys<Data>(data: Data): SetSnakeToCamelCaseNested<Data> {
  if (typeof data === 'function' || typeof data !== 'object' || data === null) {
    return data as SetSnakeToCamelCaseNested<Data>;
  }

  if (Array.isArray(data)) {
    return data.map(toCamelKeys) as SetSnakeToCamelCaseNested<Data>;
  }

  const result = {} as SetSnakeToCamelCaseNested<Data>;

  Object.entries(data).forEach(([key, value]) => {
    result[toCamel(key) as keyof typeof result] = toCamelKeys(value);
  });

  return result;
}

/**
 * Formats string from camelCase into snake_case but ignore number
 *
 * @param str
 * @returns
 */
export function toSnake(str: string, withNumber = false) {
  return str.replace(withNumber ? /[0-9A-Z]/g : /[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

type ToSnakeKeysBaseOption<Data> = {
  keepKeys?: Data extends object ? ExtractNestedKey<Data>[] : never;
};
type ToSnakeKeysWithNumberOption<Data> = ToSnakeKeysBaseOption<Data> & { withNumber: true };
type ToSnakeKeysWithoutNumberOption<Data> = ToSnakeKeysBaseOption<Data> & { withNumber: false };
type ToSnakeKeysOption<Data> = ToSnakeKeysBaseOption<Data> & { withNumber?: boolean };

/**
 * The intermediate of toSnakeKeys
 * provide richer param to assist object keys checking
 *
 * @param data
 * @param currentKey
 * @param option
 * @returns
 */
function toSnakeKeysIntermediary<Data>(
  data: Data,
  currentKey: string,
  option?: ToSnakeKeysOption<Data>,
): SetCamelToSnakeCaseNested<Data> {
  if (typeof data === 'function' || typeof data !== 'object' || data === null) {
    return data as SetCamelToSnakeCaseNested<Data>;
  }

  if (Array.isArray(data)) {
    return data.map((item) =>
      toSnakeKeysIntermediary(item, currentKey, option),
    ) as SetCamelToSnakeCaseNested<Data>;
  }

  const result = {} as SetCamelToSnakeCaseNested<Data>;

  Object.entries(data).forEach(([key, value]) => {
    const targetKey = currentKey ? `${currentKey}.${key}` : key;
    const isKept = option?.keepKeys?.includes(targetKey as ExtractNestedKey<object>);

    result[toSnake(key, option?.withNumber) as keyof typeof result] = isKept
      ? value
      : toSnakeKeysIntermediary(value, targetKey, option);
  });

  return result;
}

export function toSnakeKeys<Data>(
  data: Data,
  options?: ToSnakeKeysWithoutNumberOption<Data>,
): SetCamelToSnakeCaseNested<Data>;
export function toSnakeKeys<Data>(
  data: Data,
  options?: ToSnakeKeysWithNumberOption<Data>,
): SetCamelToSnakeCaseNested<Data, true>;
export function toSnakeKeys<Data>(
  data: Data,
  options?: ToSnakeKeysOption<Data>,
): SetCamelToSnakeCaseNested<Data>;

/**
 * Formats all keys into snake_case
 *
 * @param data
 * @param option
 * @returns
 */
export function toSnakeKeys<Data>(
  data: Data,
  option?: ToSnakeKeysOption<Data>,
): SetCamelToSnakeCaseNested<Data> {
  return toSnakeKeysIntermediary<Data>(data, '', option);
}
